import { Box, Button, Card, Group, Text } from '@mantine/core';
import { IconCloudOff } from '@tabler/icons-react';

/** Props for {@link ErrorTile}. */
export interface ErrorTileProps {
  /** What failed to load, in a few words (e.g. `"this show's totals"`). Rendered as "Couldn't load {what}". */
  what: string;
  /** Optional second line. Defaults to a reassurance that nothing was lost — most tile failures here are read failures against a still-intact ledger. */
  detail?: string;
  /** Retry handler. Omit to hide the retry button (e.g. a tile with no independent refetch). */
  onRetry?: () => void;
}

/**
 * ErrorTile — degrades ONE tile, never the page.
 *
 * Shell: any.
 *
 * Genuine design-system gap, adopted per `docs/redesign-decision-brief.md`
 * §2.2. Use this wherever one card/section of a dashboard or report fails to
 * load while its siblings keep working — never replace a whole page with an
 * error screen when only one query failed. See #213 and #434 for the prior
 * art this generalises: per-tile error handling already ships on `/inventory`
 * and needs to be a shared pattern rather than invented per surface.
 */
export function ErrorTile({
  what,
  detail = 'Nothing was lost — the underlying records are safe.',
  onRetry,
}: ErrorTileProps) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group gap={10} wrap="nowrap" align="flex-start">
        <IconCloudOff size={18} stroke={1.75} color="var(--mantine-color-dimmed)" aria-hidden />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fz={13} fw={600}>
            Couldn&rsquo;t load {what}
          </Text>
          <Text fz={12} c="dimmed">
            {detail}
          </Text>
        </Box>
        {onRetry != null && (
          <Button size="xs" variant="default" h="var(--tap-min)" onClick={onRetry}>
            Retry
          </Button>
        )}
      </Group>
    </Card>
  );
}
